import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { getLocalStorage, setLocalStorage } from '../../utils';

export interface LeadsState {
  isLoading: boolean;
  leadIds: any;
  leadData: any;
  userInfo: any;
  documentId: string | null;
  bsaStatus: 'idle' | 'pending' | 'complete' | 'failed' | 'success';
  bsaPopupViewed: boolean;
}

interface LeadData {
  leadId: string;
  productType: string;
  lastInterestAt: string;
  extraInfo: Record<string, any>;
}

const initialState: LeadsState = {
  isLoading: false,
  leadIds: {},
  leadData: {},
  userInfo: {},
  documentId: getLocalStorage('documentId') || null, // Restore from storage,
  bsaStatus: 'idle',
  bsaPopupViewed: false,
};

export const leadsSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    getLeadId: (state, actions) => {},
    updateLead: (state, actions) => {},
    leadsLoadingState: (state, action) => {
      state.isLoading = action.payload.loaderState;
    },
    saveLeadId: (state, action) => {
      state.leadIds = { ...state.leadIds, ...action.payload };
    },
    fetchLeadsData: (state, action) => {},
    saveLeadData: (state, action) => {
      const leads = action?.payload || [];
      // Create an object for leadIds and leadData
      // Declare leadIds and leadData with appropriate types
      let leadIds: { [key: string]: string } = {}; // leadIds will map productType to leadId
      let leadData: { [key: string]: LeadData } = {}; // leadData will map productType to leadData

      // Iterate over the leads and populate leadIds and leadData
      leads?.length &&
        leads?.forEach((lead: any) => {
          const { leadId, productType, lastInterestAt, extraInfo } = lead;
          // Parse the extraInfo if it's a string
          let extraInfoObject = {};
          if (lead.extraInfo && typeof lead.extraInfo === 'string') {
            try {
              extraInfoObject = JSON.parse(lead.extraInfo);
            } catch (error) {
              console.error('Error parsing extraInfo: ', error);
            }
          }
          // Save leadId in leadIds based on productType
          leadIds[productType] = leadId;
          // Save leadData, merging lead details with extraInfo
          leadData[productType] = {
            leadId,
            productType,
            lastInterestAt,
            extraInfo: extraInfoObject,
          };
        });
      state.leadIds = leadIds;
      state.leadData = leadData;
    },
    saveUserInfo: (state, action) => {
      state.userInfo = { ...state.userInfo, ...action.payload };
    },
    fetchGSTData: (state, action) => {},
    fetchGSTInfo: (state, action) => {},
    sendUserInfo: (state, action) => {},
    resetLeads: (state) => {
      state.leadIds = {};
      state.leadData = {};
      state.userInfo = {};
    },
    uploadDocument: (state, action) => {},
    getDocuments: (state, action) => {},
    deleteDocument: (state, action) => {},
    setDocumentId: (state, action: PayloadAction<string>) => {
      state.documentId = action.payload;
      state.bsaStatus = 'pending'; // Start polling when documentId is set
      state.bsaPopupViewed = false;
      setLocalStorage('documentId', action.payload); // Store in localStorage
    },
    setBsaStatus: (
      state,
      action: PayloadAction<'pending' | 'complete' | 'failed' | 'success'>
    ) => {
      state.bsaStatus = action.payload;
      if (action.payload !== 'pending') {
        localStorage.removeItem('documentId'); // Clear storage when completed/failed
      }
    },
    setBSAPopup: (state, action) => {
      state.bsaPopupViewed = action.payload;
    },
    resetPolling: (state) => {
      state.bsaStatus = 'idle';
      state.bsaPopupViewed = false;
      // Don't reset documentId here, so it persists across refreshes
    },
    clearPollingData: (state) => {
      state.bsaPopupViewed = false;
      state.bsaStatus = 'idle';
      state.documentId = '';
    },
  },
});

export const {
  getLeadId,
  updateLead,
  leadsLoadingState,
  saveLeadId,
  resetLeads,
  saveLeadData,
  sendUserInfo,
  fetchLeadsData,
  uploadDocument,
  fetchGSTData,
  fetchGSTInfo,
  deleteDocument,
  saveUserInfo,
  getDocuments,
  setDocumentId,
  setBsaStatus,
  setBSAPopup,
  resetPolling,
  clearPollingData,
} = leadsSlice.actions;
export default leadsSlice.reducer;
